import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, Search, X } from 'lucide-react';
import axiosInstance from 'shared/services/api/axiosInstance';
import TrodoLoader from 'shared/components/ui/TrodoLoader';
import DrawerScrim, { DRAWER_OVERLAY_CLASS } from 'shared/components/ui/DrawerScrim';
import { HighlightContext, HighlightText } from 'shared/components/HighlightText';
import Eval3VerdictsPanel, { useEval3Count } from 'features/evals3/components/Eval3VerdictsPanel';
import SpanEvaluateMenu from 'features/agent-analytics/components/SpanEvaluateMenu';
import TargetSignals from 'features/issues/components/TargetSignals';
import {
  formatDate, formatDuration, formatCost, formatNumber, formatRelativeTime,
  truncate, RichValueView, JsonRawView, JsonTree, DistinctIdLink, StatusDot,
  LevelBadge, StatusCodePill,
} from 'features/agent-analytics/components/runCells';
import { KindLabel } from 'features/agent-analytics/components/spanKind';
import PromptRefLink from 'features/agent-analytics/components/PromptRefLink';
import { AnnotationList, useAnnotations } from 'features/agent-analytics/annotations';

/** True when a value has nothing meaningful to render. */
function isMeaningful(value) {
  if (value == null) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Object.keys(value).length > 0;
  return true;
}

/**
 * Trace (span) detail drawer.
 * Same shape as AgentRunDetailPage:
 *   - sunken drawer shell on the right side, contained in the page (not over top bar)
 *   - control row in the content (prev/next/close), no separate header bar
 *   - meta card with the key/value strip
 *   - tab strip: Activity · Scores · Signals — the same three the run drawer
 *     shows for a selected span, so a span reads the same whether you reached
 *     it from the trace tree or from the Spans list
 *   - Activity merges Input + Output + Attributes (with a Formatted/JSON toggle)
 */
export default function TraceDetailPage({
  spanId: initialSpanId,
  teamId,
  onClose,
  onRunClick,
  onSpanClick,
  onPrev,
  onNext,
  hasPrev = false,
  hasNext = false,
  // Which tab to land on. The grading queue opens straight to 'scores', since
  // arriving from a queue of things to grade and landing on Activity means one
  // pointless click every single time. Legacy ids are mapped below.
  initialTab,
  // The query that led here (global search): marks its matches in the
  // input/output/attributes payloads so the reason this span opened is
  // visible on arrival.
  highlightQuery = '',
  /** When true, fills the parent panel instead of docking to the viewport right edge. */
  contained = false,
}) {
  const [activeSpanId, setActiveSpanId] = useState(initialSpanId);
  useEffect(() => { setActiveSpanId(initialSpanId); }, [initialSpanId]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [span, setSpan] = useState(null);
  // Tab ids were renamed to match the run drawer (overview -> activity,
  // evaluations -> scores). Map the old names so existing deep links and the
  // grading queue keep landing where they intended.
  // 'annotations' joins the legacy list: comments live in Activity now, so a
  // Slack or inbox deep link that asks for the old tab lands where they are.
  const LEGACY_TABS = { overview: 'activity', evaluations: 'scores', annotations: 'activity' };
  const [tab, setTab] = useState(LEGACY_TABS[initialTab] || initialTab || 'activity');
  useEffect(() => {
    setTab(LEGACY_TABS[initialTab] || initialTab || 'activity');
  }, [initialTab, initialSpanId]);
  const [overviewFormat, setOverviewFormat] = useState('formatted');
  const [signalsCount, setSignalsCount] = useState(0);
  // Local search over this span's payloads. Seeded from the query that opened
  // the drawer (global search) and then owned by the user.
  const [query, setQuery] = useState(highlightQuery || '');
  useEffect(() => { setQuery(highlightQuery || ''); }, [highlightQuery]);
  // The refresh key the eval3 panel and its count share. Nothing bumps it
  // since the old evals' grading list came out of this tab (TRO-475); a
  // grade made in the new panel refreshes from inside it.
  const [evalDataTick] = useState(0);
  const { enabled: annotationsEnabled } = useAnnotations();

  const load = useCallback(async () => {
    if (!activeSpanId || !teamId) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await axiosInstance.get(
        `/agent-spans/${encodeURIComponent(activeSpanId)}`,
        { params: { team_id: teamId } }
      );
      setSpan(data?.span || null);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load span.');
    } finally {
      setLoading(false);
    }
  }, [activeSpanId, teamId]);
  useEffect(() => { load(); }, [load]);

  const v3Count = useEval3Count(teamId, 'span', span?.span_id, evalDataTick);


  // Esc closes
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Parent-span jump
  const handleParentSpanClick = useCallback(
    (parentId) => {
      if (typeof onSpanClick === 'function') onSpanClick(parentId);
      else setActiveSpanId(parentId);
    },
    [onSpanClick],
  );

  // Arriving from global search: once the payload renders, bring the first
  // <mark> into view — the match may sit deep in a long output.
  const drawerRef = useRef(null);
  const activeHighlight = (query || '').trim();
  useEffect(() => {
    if (!activeHighlight || !span) return undefined;
    const id = requestAnimationFrame(() => {
      const mark = drawerRef.current?.querySelector('mark');
      if (mark) mark.scrollIntoView({ block: 'center', behavior: 'smooth' });
    });
    return () => cancelAnimationFrame(id);
  }, [activeHighlight, span, tab, overviewFormat]);

  return (
    <div
      className={
        contained
          ? 'pointer-events-auto absolute inset-0 z-[50]'
          : DRAWER_OVERLAY_CLASS
      }
      role="region"
      aria-label="Trace detail"
    >
      {/* No scrim in `contained` mode: there it fills a parent panel
          (IssueDetailPage embeds it) and there is nothing behind to dim. */}
      {!contained ? <DrawerScrim onClose={onClose} /> : null}
      <section
        ref={drawerRef}
        className={
          contained
            ? 'pointer-events-auto relative flex h-full w-full flex-col overflow-hidden bg-[color:var(--surface-sunken)]'
            : 'pointer-events-auto relative flex h-full w-[min(85%,720px)] flex-col overflow-hidden border-l border-t border-[color:var(--border-default)] bg-surface shadow-2xl'
        }
        style={{ fontFamily: 'Inter, sans-serif' }}
      >
        {loading && !span ? (
          <div className="flex flex-1 items-center justify-center"><TrodoLoader label="Loading span" /></div>
        ) : error ? (
          <div className="m-4 rounded-md border border-[color:var(--feedback-error-bg)] bg-[color:var(--feedback-error-bg)] p-3 text-[12px] text-[color:var(--feedback-error)]">
            {error}
          </div>
        ) : span ? (
          <HighlightContext.Provider value={activeHighlight}>
          <div className="flex min-h-0 flex-1 flex-col gap-4 px-4 pt-4">
            {/* Control row: prev/next · search · close */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={onPrev}
                  disabled={!hasPrev}
                  title="Previous span"
                  className="flex h-7 w-7 items-center justify-center rounded-md text-[color:var(--text-muted)] transition-colors hover:bg-surface-hover hover:text-[color:var(--text-primary)] disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronUp size={16} />
                </button>
                <button
                  type="button"
                  onClick={onNext}
                  disabled={!hasNext}
                  title="Next span"
                  className="flex h-7 w-7 items-center justify-center rounded-md text-[color:var(--text-muted)] transition-colors hover:bg-surface-hover hover:text-[color:var(--text-primary)] disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronDown size={16} />
                </button>
              </div>
              {/* Search this span's input, output and attributes. Feeds the same
                  HighlightContext the global-search deep link uses, so matches
                  mark up in place rather than filtering anything away. */}
              <div className="relative min-w-0 flex-1">
                <Search
                  size={14}
                  className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[color:var(--text-muted)]"
                />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Escape' && query) { e.stopPropagation(); setQuery(''); } }}
                  placeholder="Search this span: input, output, attributes…"
                  className="h-8 w-full rounded-lg bg-[color:var(--surface-sunken)] pl-8 pr-7 text-[12px] text-[color:var(--text-primary)] placeholder:text-[color:var(--text-muted)] focus:outline-none focus:ring-1 focus:ring-[color:var(--trodo-primary)]"
                />
                {query ? (
                  <button
                    type="button"
                    onClick={() => setQuery('')}
                    aria-label="Clear search"
                    className="absolute right-1.5 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded text-[color:var(--text-muted)] hover:bg-surface-hover hover:text-[color:var(--text-primary)]"
                  >
                    <X size={13} />
                  </button>
                ) : null}
              </div>
              <div className="flex flex-none items-center gap-1.5">
                {span.span_id && span.kind === 'llm' ? (
                  <SpanEvaluateMenu
                    spanId={span.span_id}
                    hasPrompt={Boolean(span.attributes?.['trodo.prompt.version_hash'])}
                  />
                ) : null}
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Close"
                  className="flex h-7 w-7 items-center justify-center rounded-md text-[color:var(--text-muted)] transition-colors hover:bg-surface-hover hover:text-[color:var(--text-primary)]"
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            {/* Meta card */}
            <SpanMetaCard
              span={span}
              onRunClick={onRunClick}
              onParentSpanClick={handleParentSpanClick}
            />

            {/* Tabs + body container */}
            <div className="-mx-4 flex min-h-0 flex-1 flex-col overflow-hidden border-t border-[color:var(--border-default)] bg-surface">
              <div className="flex items-center justify-between gap-2 border-b border-[color:var(--border-default)] px-2">
                <div className="flex min-w-0 items-center gap-1 overflow-x-auto">
                  <TabButton id="activity" label="Activity" active={tab === 'activity'} onClick={setTab} />
                  <TabButton id="scores"   label="Scores"   active={tab === 'scores'}   onClick={setTab} badge={v3Count || null} />
                  <TabButton id="signals"  label="Signals"  active={tab === 'signals'}  onClick={setTab} badge={signalsCount || null} />
                </div>
                {tab === 'activity' ? (
                  <div className="inline-flex h-7 overflow-hidden rounded-md border border-[color:var(--border-default)] text-[11px]">
                    <button
                      type="button"
                      onClick={() => setOverviewFormat('formatted')}
                      className={`px-3 transition-colors ${overviewFormat === 'formatted' ? 'bg-surface-hover text-[color:var(--text-primary)] font-medium' : 'bg-surface text-[color:var(--text-secondary)] hover:bg-surface-hover'}`}
                    >
                      Formatted
                    </button>
                    <button
                      type="button"
                      onClick={() => setOverviewFormat('json')}
                      className={`px-3 transition-colors border-l border-[color:var(--border-default)] ${overviewFormat === 'json' ? 'bg-surface-hover text-[color:var(--text-primary)] font-medium' : 'bg-surface text-[color:var(--text-secondary)] hover:bg-surface-hover'}`}
                    >
                      JSON
                    </button>
                  </div>
                ) : null}
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto p-3">
                {tab === 'activity' ? (
                  <OverviewTab
                    span={span}
                    format={overviewFormat}
                    onRunClick={onRunClick}
                    onSpanClick={handleParentSpanClick}
                    annotationsEnabled={annotationsEnabled}
                    onOpenContext={(ctx) => {
                      if (ctx?.type === 'run') onRunClick?.(ctx.id);
                      else if (ctx?.type === 'span' && ctx.id && ctx.id !== span.span_id) onSpanClick?.(ctx.id);
                    }}
                  />
                ) : tab === 'signals' ? (
                  <TargetSignals teamId={teamId} kind="span" id={span.span_id} onCount={setSignalsCount} />
                ) : (
                  <div className="flex flex-col gap-4">
                    {/* The old evals' Human evals and Evaluations sections
                        stood here until TRO-475; the UI is off, the engine and
                        its data are untouched. */}
                    <Eval3VerdictsPanel teamId={teamId} target="span" unitId={span.span_id} refreshKey={evalDataTick} />
                  </div>
                )}
              </div>
            </div>
          </div>
          </HighlightContext.Provider>
        ) : null}
      </section>
    </div>
  );
}

// ── Pieces ───────────────────────────────────────────────────────────────────

function TabButton({ id, label, badge, active, onClick }) {
  return (
    <button
      type="button"
      onClick={() => onClick(id)}
      className={`relative whitespace-nowrap px-3 py-2 text-[12px] font-medium transition-colors ${
        active ? 'text-[color:var(--text-primary)]' : 'text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]'
      }`}
    >
      <span className="inline-flex items-center gap-1.5">
        {label}
        {badge != null ? (
          <span className={`rounded-full px-1.5 text-[10px] font-semibold ${
            active ? 'bg-surface-hover text-[color:var(--text-primary)]' : 'bg-[color:var(--border-subtle)] text-[color:var(--text-secondary)]'
          }`}>
            {badge}
          </span>
        ) : null}
      </span>
      <span className={`absolute inset-x-2 bottom-0 h-[2px] rounded-t ${active ? 'bg-[color:var(--text-primary)]' : 'bg-transparent'}`} />
    </button>
  );
}

function SpanMetaCard({ span, onRunClick, onParentSpanClick }) {
  const cells = useMemo(() => {
    // Prompt traceability — the exact managed-prompt version this span ran,
    // stamped by the SDK. Pinned by immutable hash, so it's the version that
    // actually ran even if the label has since moved. See trodo.prompt.* attrs.
    const attrs = span.attributes || {};
    const promptName = attrs['trodo.prompt.name'];
    const promptHash = attrs['trodo.prompt.version_hash'];
    const c = [
      // Name/tool/model route through HighlightText: a global-search hit on a
      // span's NAME should mark it here — the payload may not contain the term.
      { label: 'Name',     content: <span title={span.name}><HighlightText text={truncate(span.name, 32)} /></span> },
      { label: 'Kind',     content: <KindLabel kind={span.kind} /> },
      { label: 'Status',   content: (
        <span className="inline-flex flex-wrap items-center gap-2">
          <StatusLine status={span.status} />
          <LevelBadge level={span.level} status={span.status} />
          <StatusCodePill code={span.status_code} />
        </span>
      ) },
      span.duration_ms != null && {
        label: 'Duration', content: <span className="tabular-nums">{formatDuration(span.duration_ms)}</span>,
      },
      span.agent_name && { label: 'Agent',     content: <span><HighlightText text={span.agent_name} /></span> },
      span.tool_name  && { label: 'Tool',      content: <span className="font-mono text-[11px]"><HighlightText text={truncate(span.tool_name, 24)} /></span> },
      promptName && {
        label: 'Prompt',
        content: (
          <PromptRefLink name={String(promptName)} versionHash={promptHash} />
        ),
      },
      span.distinct_id && { label: 'User',      content: <DistinctIdLink value={span.distinct_id} /> },
      span.conversation_id && {
        label: 'Session', content: <span className="font-mono text-[11px]" title={span.conversation_id}>{truncate(span.conversation_id, 16)}</span>,
      },
      {
        label: 'Run',
        content: span.run_id ? (
          typeof onRunClick === 'function' ? (
            <button
              type="button"
              onClick={() => onRunClick(span.run_id)}
              className="font-mono text-[11px] text-[color:var(--text-primary)] underline-offset-2 hover:underline"
              title={`Open run ${span.run_id}`}
            >
              {truncate(span.run_id, 14)}
            </button>
          ) : (
            <span className="font-mono text-[11px] text-[color:var(--text-secondary)]" title={span.run_id}>{truncate(span.run_id, 14)}</span>
          )
        ) : (
          <span className="rounded bg-surface-hover px-1.5 py-0.5 font-mono text-[10px] text-[color:var(--text-muted)]">runless</span>
        ),
      },
      // The parent is usually NOT a stored span. OTLP ingest folds the
      // parentless root span into the agent_runs row and never writes it to
      // agent_spans, so its children point at an id that resolves to nothing —
      // 100% of spans on a live team. Rendering that as "Parent span" and
      // fetching it guaranteed a 404 on every click. When the backend says the
      // parent row is absent, the parent IS the run: say so, and open the run.
      span.parent_span_id && (
        span.parent_span_exists
          ? {
            label: 'Parent span',
            content: typeof onParentSpanClick === 'function' ? (
              <button
                type="button"
                onClick={() => onParentSpanClick(span.parent_span_id)}
                className="font-mono text-[11px] text-[color:var(--text-primary)] underline-offset-2 hover:underline"
                title={`Open parent span ${span.parent_span_id}`}
              >
                {truncate(span.parent_span_id, 14)}
              </button>
            ) : (
              <span className="font-mono text-[11px]">{truncate(span.parent_span_id, 14)}</span>
            ),
          }
          : {
            label: 'Parent',
            content: span.run_id && typeof onRunClick === 'function' ? (
              <button
                type="button"
                onClick={() => onRunClick(span.run_id)}
                className="font-mono text-[11px] text-[color:var(--text-primary)] underline-offset-2 hover:underline"
                title={`The parent of this span is the run itself: open run ${span.run_id}`}
              >
                this run
              </button>
            ) : (
              <span className="text-[11px] text-[color:var(--text-secondary)]">this run</span>
            ),
          }
      ),
      span.started_at && {
        label: 'Started',
        content: <span className="tabular-nums" title={formatDate(span.started_at)}>{formatRelativeTime(span.started_at)}</span>,
      },
      // LLM-specific fields appear inline when present
      span.kind === 'llm' && span.model     && { label: 'Model',    content: <span className="font-mono text-[11px]"><HighlightText text={truncate(span.model, 18)} /></span> },
      span.kind === 'llm' && span.provider  && { label: 'Provider', content: <span>{span.provider}</span> },
      span.kind === 'llm' && (span.input_tokens != null || span.output_tokens != null) && {
        label: 'Tokens', content: <span className="tabular-nums">{formatNumber(span.input_tokens)} / {formatNumber(span.output_tokens)}</span>,
      },
      span.kind === 'llm' && span.cost != null && {
        label: 'Cost', content: <span className="tabular-nums">{formatCost(span.cost)}</span>,
      },
    ].filter(Boolean);
    return c;
  }, [span, onRunClick, onParentSpanClick]);

  return (
    <div className="pb-1">
      <div className="grid gap-x-6 gap-y-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))' }}>
        {cells.map((cell, i) => (
          <MetaCell key={i} label={cell.label}>{cell.content}</MetaCell>
        ))}
      </div>
    </div>
  );
}

function MetaCell({ label, children }) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <span className="text-[11px] text-[color:var(--text-muted)]">{label}</span>
      <div className="truncate text-[13px] text-[color:var(--text-primary)]">{children}</div>
    </div>
  );
}

function StatusLine({ status }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <StatusDot status={status} />
      <span className="capitalize">{status || '—'}</span>
    </span>
  );
}

function OverviewTab({ span, format, onRunClick, onSpanClick, annotationsEnabled, onOpenContext }) {
  const entityProps = { onRunClick, onSpanClick };
  const hasInput  = isMeaningful(span.input);
  const hasOutput = isMeaningful(span.output);
  const hasAttrs  = isMeaningful(span.attributes);
  const hasError = span.status === 'error'
    || !!span.error_message || !!span.error_type || !!span.status_code || !!span.stack_trace;
  const empty = !hasInput && !hasOutput && !hasAttrs && !hasError;

  // Comments render even on a span with no payload — "nothing was captured
  // here" is exactly the kind of span someone leaves a note on.
  const comments = annotationsEnabled ? (
    <Section title="Comments">
      <AnnotationList
        layout="section"
        conversationId={span.conversation_id}
        scope="span"
        runId={span.run_id}
        spanId={span.span_id}
        spanStartedAt={span.started_at}
        lockedContext={{
          type: 'span',
          id: span.span_id,
          runId: span.run_id,
          startedAt: span.started_at,
          label: span.name || span.tool_name || span.span_id,
        }}
        onOpenContext={onOpenContext}
      />
    </Section>
  ) : null;

  if (empty) {
    return (
      <div className="flex flex-col gap-3 text-[12px]">
        <p className="text-[12px] text-[color:var(--text-muted)]">No input, output or attributes recorded for this span.</p>
        {comments}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 text-[12px]">
      {hasError ? (
        <Section title="Error">
          <div className="rounded-md border border-[color:var(--feedback-error-bg)] bg-[color:var(--feedback-error-bg)] p-3 text-[color:var(--feedback-error)]">
            {(span.error_type || span.status_code) ? (
              <div className="mb-1.5 flex flex-wrap items-center gap-2">
                {span.error_type ? <span className="font-mono text-[11px] font-semibold">{span.error_type}</span> : null}
                <StatusCodePill code={span.status_code} />
                <LevelBadge level={span.level} status="error" />
              </div>
            ) : null}
            {span.error_message
              ? <p className="whitespace-pre-wrap break-words">{span.error_message}</p>
              : <p className="opacity-70">Marked as errored (no message recorded).</p>}
            {span.stack_trace ? (
              <details className="mt-2">
                <summary className="cursor-pointer text-[11px] opacity-80">Stack trace</summary>
                {/* --state-hover-bg, not bg-black/10: a 10% black wash over an
                    already near-black panel is invisible, so the stack trace
                    had no block of its own in dark mode. The token is a dark
                    tint in light and a light one in dark. */}
                <pre className="mt-1 max-h-64 overflow-auto whitespace-pre-wrap break-words rounded bg-[color:var(--state-hover-bg)] p-2 font-mono text-[10px] leading-snug">
                  {span.stack_trace}
                </pre>
              </details>
            ) : null}
          </div>
        </Section>
      ) : null}
      {hasInput  ? <Section title="Input">
        {format === 'formatted' ? <RichValueView value={span.input} {...entityProps} /> : <JsonRawView value={span.input} {...entityProps} />}
      </Section> : null}
      {hasOutput ? <Section title="Output">
        {format === 'formatted' ? <RichValueView value={span.output} {...entityProps} /> : <JsonRawView value={span.output} {...entityProps} />}
      </Section> : null}
      {hasAttrs  ? <Section title="Attributes">
        {format === 'formatted'
          ? <JsonTree value={span.attributes} {...entityProps} />
          : <JsonRawView value={span.attributes} {...entityProps} />}
      </Section> : null}
      {comments}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]">{title}</p>
      <div className="rounded-md border border-[color:var(--border-default)] bg-surface p-3 text-[12px]">{children}</div>
    </section>
  );
}
